import { MimeType } from 'vet-bones/constants';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { FileExtension } from 'src/constants/fileExtensions';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { dataSourceTypeDescriptionSelector } from 'src/selectors/dataSourceTypeDescription';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { Connection } from 'src/store/connections/types';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { exportModel } from 'src/utils/export/model';
import { exportResources } from 'src/utils/export/resources';
import { exportRules } from 'src/utils/export/rules';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { encodeProject } from 'src/utils/exportProject/encodeProject';
import { ExportToFileOptions, exportToZipFile } from 'src/utils/exportToFile';
import { stringToFilename } from 'src/utils/stringToFilename';

export const exportProject =
  (projectId: string, connection: Connection | null) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();
    const projectToExport = state.projects.projectsById[projectId];
    const modelsById = projectModelsByIdSelector(projectId)(getState());
    const typeDescription =
      dataSourceTypeDescriptionSelector(connection)(state);

    if (!projectToExport) {
      return;
    }
    // remove cloud project data from the project
    const project = {
      ...projectToExport,
      isCloudProject: undefined,
      cloudProjectLastUpdated: undefined,
      cloudProjectId: undefined,
    };

    // Export models & rules
    const modelFiles: ExportToFileOptions[] = [];
    const modelRulesFiles: ExportToFileOptions[] = [];
    const combinedModelFile = exportModel({
      name: project.name,
      model: project.diagram.model,
      projectModelIds: new Set(Object.keys(modelsById)),
      projectPropertyType: project.propertyType,
      targetModelId: project.targetModelId,
    });
    modelFiles.push(combinedModelFile);

    const usedNames = new Set<string>();
    const getUniqueName = (name: string) => {
      let fixedName = name;
      let num = 0;
      while (usedNames.has(fixedName)) {
        num += 1;
        fixedName = `${name}_${num}`;
      }
      return fixedName;
    };

    const ruleModel = getQueryBuilderModel(project.diagram.model);
    const uniqueNamesByModelId: Record<string, string> = {};
    Object.values(modelsById).forEach((projectModel) => {
      const uniqueModelName = getUniqueName(projectModel.name);
      uniqueNamesByModelId[projectModel.id] = uniqueModelName;
      const modelFile = exportModel({
        fileNamePrefix: 'models/',
        name: uniqueModelName,
        model: project.diagram.model,
        projectModelIds: new Set([projectModel.id]),
        projectPropertyType: project.propertyType,
        targetModelId: project.targetModelId,
      });
      modelFiles.push(modelFile);

      const modelRulesById = Object.values(
        project.diagram.model.modelData.rulesById
      ).reduce<Record<string, ModelRule>>((acc, rule) => {
        if (rule.models.some((ruleModel) => projectModel.id === ruleModel)) {
          acc[rule.id] = rule;
        }
        return acc;
      }, {});
      const rulesFile = exportRules(
        uniqueModelName,
        ruleModel,
        modelRulesById,
        'rules/'
      );
      if (rulesFile) {
        modelRulesFiles.push(rulesFile);
      }
    });

    const topRulesFile = exportRules(
      project.name,
      ruleModel,
      project.diagram.model.modelData.rulesById
    );

    const { classMappingsByResource, relationshipMappingsByResource } =
      getValidMappingsByResource(project.diagram.model);
    const { fileUploadExports, virtualGraphExports } = exportResources({
      project,
      classMappingsByResource,
      relationshipMappingsByResource,
      typeDescription,
    });

    const fileUploadFiles = fileUploadExports.flatMap(({ folderName, files }) =>
      Object.values(files).map((file) => ({
        ...file,
        filename: `${folderName}/${file.filename}`,
      }))
    );
    const virtualGraphFiles = virtualGraphExports.flatMap(
      ({ folderName, files }) =>
        Object.values(files).map((file) => ({
          ...file,
          filename: `${folderName}/${file.filename}`,
        }))
    );

    const projectContents = encodeProject(project, CURRENT_MIGRATION_VERSION);
    if (!projectContents) {
      // if we fail to generate the project file, fail the whole export
      dispatch(projectsActionCreators.exportedProject(projectId, null));
      return;
    }

    const projectFileName = stringToFilename(project.name);
    const designerProjectFile = {
      contents: projectContents,
      filename: `${projectFileName}${FileExtension.STARDOG_DESIGNER}`,
      mimeType: MimeType.TEXT,
    };

    const zipFileName = `${projectFileName}${FileExtension.ZIP}`;
    await exportToZipFile({
      filename: zipFileName,
      files: [
        ...modelFiles,
        topRulesFile,
        ...modelRulesFiles,
        designerProjectFile,
        ...fileUploadFiles,
        ...virtualGraphFiles,
      ].filter((file): file is ExportToFileOptions => Boolean(file)),
    });

    dispatch(projectsActionCreators.exportedProject(projectId, zipFileName));
  };
